import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import axiosInstance from '../../axiosInstance';
import { UNITES, euros, nombre, unitePropre } from './constantes';
import { themeExpert } from '../../theme/expert';

/**
 * Une ligne de chiffrage : un libellé, un prix unitaire, une quantité.
 *
 * La recherche au barème vit ici plutôt qu'au niveau de l'écran : elle était
 * partagée entre la fenêtre d'ajout et celle de modification, si bien qu'une
 * recherche tapée dans l'une restait affichée dans l'autre.
 */
export default function DetailDialog({ ouvert, onFermer, ligne, setLigne,
                                       onEnregistrer, occupe }) {
  const [recherche, setRecherche] = useState('');
  const [propositions, setPropositions] = useState([]);

  useEffect(() => {
    if (!ouvert) { setRecherche(''); setPropositions([]); }
  }, [ouvert]);

  useEffect(() => {
    if (!recherche) { setPropositions([]); return undefined; }
    const minuterie = setTimeout(() => {
      axiosInstance.get('/pricinglist', { params: { query: recherche } })
        .then(({ data }) => setPropositions(data.results || data))
        .catch(() => setPropositions([]));
    }, 300);
    return () => clearTimeout(minuterie);
  }, [recherche]);

  if (!ligne) return null;

  const prix = nombre(ligne.unitValue);
  const quantite = nombre(ligne.quantity);
  const total = prix * quantite;

  const poser = (champ, valeur) =>
    setLigne((etat) => ({ ...etat, [champ]: valeur }));

  const reprendreDuBareme = (entree) => {
    if (!entree) return;
    setLigne((etat) => ({
      ...etat,
      title: entree.title,
      unitValue: nombre(entree.unityMax),
      // Le barème écrit ses unités à la main : on les ramène à la liste,
      // ici comme à l'ajout — l'écran de modification ne le faisait pas, et
      // enregistrait une unité que le rapport ne sait pas rendre.
      unit: unitePropre(entree.unit),
    }));
  };

  const fourchette = (entree) => {
    const min = nombre(entree.unityMin);
    const max = nombre(entree.unityMax);
    if (!min && !max) return '';
    return min && max && min !== max
      ? `${euros(min)} – ${euros(max)}` : euros(max || min);
  };

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="md">
        <Box component="form"
             onSubmit={(e) => { e.preventDefault();
                                onEnregistrer({ ...ligne, total }); }}
             noValidate>
          <DialogTitle>
            {ligne.id ? 'Modifier la ligne' : 'Ajouter une ligne'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <Autocomplete
                options={propositions}
                getOptionLabel={(o) => o.title || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                filterOptions={(x) => x}
                inputValue={recherche}
                onInputChange={(_, v) => setRecherche(v)}
                onChange={(_, v) => reprendreDuBareme(v)}
                renderOption={(props, entree) => (
                  <li {...props} key={entree.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{entree.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[fourchette(entree), entree.unit]
                          .filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Chercher au barème"
                             helperText="Le libellé, le prix et l'unité se remplissent seuls. Vous restez libre de les corriger." />
                )}
              />

              <TextField label="Libellé" value={ligne.title || ''}
                         onChange={(e) => poser('title', e.target.value)}
                         required />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Prix unitaire"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={ligne.unitValue ?? ''}
                  onChange={(e) => poser('unitValue', e.target.value)}
                  required
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">€</InputAdornment>) }}
                  helperText="Hors TVA."
                />
                <TextField
                  label="Quantité"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={ligne.quantity ?? ''}
                  onChange={(e) => poser('quantity', e.target.value)}
                  required
                  helperText=" "
                />
                <TextField select label="Unité" value={ligne.unit || 'm2'}
                           onChange={(e) => poser('unit', e.target.value)}
                           sx={{ minWidth: 140 }} helperText=" ">
                  {UNITES.map(({ valeur, libelle }) => (
                    <MenuItem key={valeur} value={valeur}>{libelle}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Box sx={{ bgcolor: 'background.default', borderRadius: 1,
                         p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary"
                            component="div">
                  Total de la ligne
                </Typography>
                <Typography variant="h2">{euros(total)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {euros(prix)} × {quantite}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onFermer} disabled={occupe}>Annuler</Button>
            <Button type="submit" variant="contained"
                    disabled={occupe || !ligne.title?.trim()}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
}
